import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface User {
  id: string;
  email: string;
  role: 'system_admin' | 'ngo_admin' | 'ngo_manager' | 'ngo_field_worker';
  firstName: string;
  lastName: string;
  organizationId?: string;
  organizationName?: string;
  userType?: string;
  profilePhotoUrl?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await fetch('/api/auth/me', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            setUser({
              id: data.data.user.id,
              email: data.data.user.email,
              role: data.data.user.role,
              firstName: data.data.user.first_name,
              lastName: data.data.user.last_name,
              organizationId: data.data.user.organization_id,
              organizationName: data.data.user.organization_name,
              userType: data.data.user.user_type,
              profilePhotoUrl: data.data.user.profile_photo_url
            });
          } else {
            // Token invalid, remove it
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Auth check failed:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Login failed');
      }

      // Store token and user data
      localStorage.setItem('token', data.data.token);
      setUser({
        id: data.data.user.id,
        email: data.data.user.email,
        role: data.data.user.role,
        firstName: data.data.user.firstName,
        lastName: data.data.user.lastName,
        organizationId: data.data.user.organizationId,
        organizationName: data.data.user.organizationName,
        userType: data.data.user.userType,
        profilePhotoUrl: data.data.user.profilePhotoUrl
      });

      toast.success(t('auth.welcomeBack', { name: data.data.user.firstName }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success(t('auth.loggedOut'));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 